import { useDifficulties } from "~/hooks/useDifficulties"
import { ChevronDownIcon, UserCircleIcon } from "lucide-react"
import {
	type ComponentProps,
	type Dispatch,
	type ReactNode,
	type SetStateAction,
	useState
} from "react"
import { Button } from "~/components/ui/button"
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList
} from "~/components/ui/command"
import {
	Popover,
	PopoverContent,
	PopoverTrigger
} from "~/components/ui/popover"
import { usePermission } from "~/hooks/usePermission"
import type { IssueDifficulty, IssuePriority, IssueStatus } from "~/lib/enums"
import { TeamPermissions } from "~/lib/permissions"
import { cn } from "~/lib/utils"
import type { RouterOutputs } from "~/trpc/react"
import { DifficultyIcon } from "../icons/difficulty"
import { PriorityIcon, priorityConfigs } from "../icons/priority"
import { StatusIcon, statusConfigs } from "../icons/status"
import { EntityIdentity } from "~/components/EntityIdentity"

export function issueSettingAssignees(
	members?: NonNullable<RouterOutputs["team"]["getBySlug"]>["teamMembers"]
) {
	return [
		{
			value: null,
			searchText: "Unassigned",
			display: (
				<div className="flex flex-row gap-2 items-center">
					<UserCircleIcon className="size-4" />
					<span>Unassigned</span>
				</div>
			)
		},
		...(members?.map((member) => ({
			value: member.id,
			searchText: member.orgMember.user.name,
			display: (
				<EntityIdentity
					kind="user"
					userId={member.orgMember.userId}
					name={member.orgMember.user.name}
					image={member.orgMember.user.image}
					size="sm"
				/>
			)
		})) ?? [])
	]
}

export function IssueSetting<T extends string | number | null>({
	placeholder,
	value,
	setValue,
	onChange,
	withSearch = false,
	disabled = false,
	showArrow = false,
	options,
	customNotFound = "Not found",
	activeTeamId,
	shape = "default",
	showBorder = false,
	size = "sm",
	triggerClassName,
	trigger,
	ariaLabel,
	requireTeam = true,
	popoverSize = "md",
	searchPlaceholder = "Search..."
}: {
	value: T
	setValue?: Dispatch<SetStateAction<T>>
	onChange?: (value: T) => void
	placeholder?: ReactNode
	withSearch?: boolean
	disabled?: boolean
	showArrow?: boolean
	customNotFound?: string
	activeTeamId?: string | null
	shape?: "default" | "pill"
	showBorder?: boolean
	size?: "sm" | "compact" | "icon-md"
	triggerClassName?: string
	trigger?: ReactNode
	ariaLabel?: string
	requireTeam?: boolean
	popoverSize?: ComponentProps<typeof PopoverContent>["size"]
	searchPlaceholder?: string
} & (
	| {
			options: {
				value: T
				display: ReactNode
				searchText?: string
			}[]
	  }
	| {
			options: "status" | "priority" | "difficulty"
	  }
)) {
	const difficulties = useDifficulties(activeTeamId)
	const [open, setOpen] = useState(false)
	const { hasTeamPermission, teamLoaded } = usePermission({
		activeTeamId
	})

	const canCreate = hasTeamPermission(TeamPermissions.CREATE_ISSUES)
	const canQueue = hasTeamPermission(TeamPermissions.CREATE_QUEUE_ISSUES)

	const optionsParsed =
		options === "status"
			? (canQueue && !canCreate
					? Object.keys(statusConfigs).filter((x) => x !== "Done")
					: Object.keys(statusConfigs)
				).map((x) => ({
					value: x as IssueStatus,
					display: <StatusIcon status={x as IssueStatus} withText={true} />
				}))
			: options === "priority"
				? Object.keys(priorityConfigs).map((x) => ({
						value: x as IssuePriority,
						display: (
							<PriorityIcon priority={x as IssuePriority} withText={true} />
						)
					}))
				: options === "difficulty"
					? [{ level: null, label: "None" }, ...difficulties].map((option) => ({
							value: option.level,
							display: (
								<DifficultyIcon
									difficulty={option.level}
									label={option.label}
									withText={true}
								/>
							)
						}))
					: Array.isArray(options)
						? options
						: []

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button
					shape={shape}
					variant={showBorder ? "picker-outline" : "picker"}
					role="combobox"
					aria-label={ariaLabel}
					aria-expanded={open}
					size={size}
					disabled={disabled || (requireTeam && !teamLoaded)}
					className={cn(size !== "icon-md" && "w-full", triggerClassName)}
				>
					{trigger ??
						optionsParsed.find((option) => option.value === value)?.display ??
						(options === "difficulty" && typeof value === "number" ? (
							<DifficultyIcon
								difficulty={value as IssueDifficulty}
								teamId={activeTeamId}
								withText
							/>
						) : (
							(placeholder ?? "Select…")
						))}
					{showArrow && <ChevronDownIcon className="ml-auto size-4" />}
				</Button>
			</PopoverTrigger>
			<PopoverContent size={popoverSize}>
				<Command>
					{withSearch && <CommandInput placeholder={searchPlaceholder} />}
					<CommandList>
						<CommandEmpty>{customNotFound}</CommandEmpty>
						<CommandGroup>
							{optionsParsed.map((option, index) => (
								<CommandItem
									key={option.value ?? `null-${index}`}
									value={
										option.value == null ? undefined : String(option.value)
									}
									keywords={
										"searchText" in option &&
										typeof option.searchText === "string"
											? [option.searchText]
											: typeof option.display === "string"
												? [option.display]
												: undefined
									}
									onSelect={() => {
										const typedValue = option.value as T
										if (setValue) {
											setValue(typedValue)
										}
										if (onChange) {
											onChange(typedValue)
										}
										setOpen(false)
									}}
								>
									{option.display}
								</CommandItem>
							))}
						</CommandGroup>
					</CommandList>
				</Command>
			</PopoverContent>
		</Popover>
	)
}
